import React from 'react';
import { Box, Typography } from '@mui/material';

export type CardSelectorOption = {
  id: string;
  title: string;
  description: string;
  disabled?: boolean;
};

type Props = {
  /** Accessible name for the radiogroup. */
  name: string;
  sectionTitle: string;
  sectionDescription: string;
  options: readonly CardSelectorOption[];
  value: string;
  onChange?: (id: string) => void;
  columns?: {
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
  };
  dataTestId?: string;
};

const cardSx = (isSelected: boolean, isDisabled: boolean) => ({
  boxSizing: 'border-box' as const,
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'flex-start',
  gap: 0.75,
  width: '100%',
  minHeight: 112,
  p: 2,
  border: '1px solid',
  borderColor: isSelected ? 'link.main' : 'border.main',
  borderRadius: '4px',
  backgroundColor: isSelected ? 'brand.light' : 'background.paper',
  textAlign: 'left' as const,
  cursor: isDisabled ? 'not-allowed' : 'pointer',
  opacity: isDisabled ? 0.55 : 1,
  font: 'inherit',
  transition: 'border-color 0.15s ease, background-color 0.15s ease',
  '&:hover': isDisabled
    ? undefined
    : {
        borderColor: isSelected ? 'link.main' : 'primary.main',
      },
});

export const CardSelector = ({
  name,
  sectionTitle,
  sectionDescription,
  options,
  value,
  onChange,
  columns = { xs: 1, sm: 2, md: 3, lg: 4 },
  dataTestId,
}: Props): JSX.Element => (
  <Box component="section" data-testid={dataTestId}>
    <Typography
      component="h3"
      sx={{
        mb: 1,
        color: 'text.primary',
        fontFamily: 'Poppins, sans-serif',
        fontSize: 14,
        fontWeight: 600,
        lineHeight: '18px',
      }}
    >
      {sectionTitle}
    </Typography>
    <Typography variant="small" color="text.secondary" sx={{ mb: 2 }}>
      {sectionDescription}
    </Typography>

    <Box
      role="radiogroup"
      aria-label={name}
      sx={{
        display: 'grid',
        gap: 1.5,
        gridTemplateColumns: {
          xs: `repeat(${columns.xs ?? 1}, minmax(0, 1fr))`,
          sm: `repeat(${columns.sm ?? 2}, minmax(0, 1fr))`,
          md: `repeat(${columns.md ?? 3}, minmax(0, 1fr))`,
          lg: `repeat(${columns.lg ?? 4}, minmax(0, 1fr))`,
        },
      }}
    >
      {options.map((option) => {
        const isSelected = option.id === value;
        const isDisabled = Boolean(option.disabled);

        return (
          <Box
            key={option.id}
            component="button"
            type="button"
            role="radio"
            aria-checked={isSelected}
            aria-disabled={isDisabled}
            disabled={isDisabled}
            data-testid={dataTestId ? `${dataTestId}-option-${option.id}` : undefined}
            onClick={() => {
              if (!isDisabled) {
                onChange?.(option.id);
              }
            }}
            sx={cardSx(isSelected, isDisabled)}
          >
            <Typography
              component="span"
              sx={{
                color: 'text.primary',
                fontFamily: 'Poppins, sans-serif',
                fontSize: 14,
                fontWeight: 600,
                lineHeight: '20px',
              }}
            >
              {option.title}
            </Typography>
            <Typography
              component="span"
              sx={{
                color: 'text.secondary',
                fontFamily: 'Poppins, sans-serif',
                fontSize: 12,
                fontWeight: 400,
                lineHeight: '16px',
              }}
            >
              {option.description}
            </Typography>
          </Box>
        );
      })}
    </Box>
  </Box>
);
